import React, { useState, useEffect } from "react";
import { getData, serverURL, postData, postDataAndImage } from "../../../Services/Fetchnodeservices";

// ===================== THEME =====================
const getT = (isDark) => ({
  bg: isDark ? "#0c1018" : "#f0f4ff",
  surface: isDark ? "#141824" : "#ffffff",
  surfaceAlt: isDark ? "#1c2133" : "#f4f7ff",
  border: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f5",
  text: isDark ? "#f1f5f9" : "#0f172a",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  textSub: isDark ? "#94a3b8" : "#64748b",
  shadow: isDark ? "0 4px 28px rgba(0,0,0,0.45)" : "0 4px 28px rgba(99,102,241,0.10)",
  shadowSm: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "0 2px 10px rgba(99,102,241,0.07)",
  accent: "#6c63ff",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
});

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </svg>
);

// ===================== HELPER FUNCTIONS =====================
const getTypeLabel = (type) => {
  const typeMap = {
    'business': 'Business / Restaurant',
    'delivery': 'Delivery Partner',
    'vendor': 'Vendor',
    'Business / Restaurant': 'Business / Restaurant',
    'Rider / Driver': 'Rider / Driver',
    'User / Customer': 'User / Customer',
    'customer': 'User / Customer'
  };
  return typeMap[type] || type || 'Business / Restaurant';
};

const getTypeValue = (type) => {
  if (['business', 'delivery', 'vendor', 'Business / Restaurant', 'Rider / Driver', 'User / Customer'].includes(type)) {
    return type;
  }
  const reverseMap = {
    'Business / Restaurant': 'business',
    'Rider / Driver': 'delivery',
    'User / Customer': 'customer'
  };
  return reverseMap[type] || type || 'business';
};

const getTypeColor = (type) => {
  const typeColors = {
    'business': { bg: "#6c63ff20", color: "#6c63ff" },
    'delivery': { bg: "#3b82f620", color: "#3b82f6" },
    'vendor': { bg: "#22c55e20", color: "#22c55e" },
    'Business / Restaurant': { bg: "#6c63ff20", color: "#6c63ff" },
    'Rider / Driver': { bg: "#f59e0b20", color: "#f59e0b" },
    'User / Customer': { bg: "#3b82f620", color: "#3b82f6" },
    'customer': { bg: "#3b82f620", color: "#3b82f6" }
  };
  return typeColors[type] || typeColors['business'];
};

// ===================== COMPONENTS =====================
const StatusBadge = ({ status, isDark }) => {
  const t = getT(isDark);
  const isActive = status === "1";
  const config = isActive 
    ? { bg: "rgba(34,197,94,0.15)", color: t.success, label: "Active" }
    : { bg: "rgba(239,68,68,0.15)", color: t.danger, label: "Inactive" };
  return (
    <span style={{ padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: config.bg, color: config.color }}>
      {config.label}
    </span>
  );
};

const Toast = ({ message, type, onClose, isDark }) => {
  const t = getT(isDark);
  return (
    <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1000, background: t.surface, border: `1px solid ${type === "error" ? t.danger : t.success}`, borderRadius: "12px", padding: "12px 20px", display: "flex", alignItems: "center", gap: "12px", boxShadow: t.shadow }}>
      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: type === "error" ? t.danger : t.success }} />
      <span style={{ fontSize: "13px", color: type === "error" ? t.danger : t.success }}>{message}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}><CloseIcon /></button>
    </div>
  );
};

// ===================== MAIN COMPONENT - MOU_AGREEMENTS =====================
const MOUAgreements = ({ isDark = true }) => {
  const t = getT(isDark);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAgreement, setViewingAgreement] = useState(null);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  
  // Data state
  const [agreements, setAgreements] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    type: "business",
    status: "1",
    sections: [],
    hide: 0
  });

  // Current section being edited
  const [currentSection, setCurrentSection] = useState({ title: "", content: "" });
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetchAllAgreements();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const parseSections = (agreement) => {
    if (!agreement) return null;
    try {
      return {
        ...agreement,
        sections: typeof agreement.sections === 'string' 
          ? JSON.parse(agreement.sections) 
          : agreement.sections || []
      };
    } catch (e) {
      console.error("Error parsing sections:", e);
      return {
        ...agreement,
        sections: []
      };
    }
  };

  const fetchAllAgreements = async () => {
    setLoading(true);
    try {
      const result = await getData("mouagreements/fetch_all_page");
      if (result.status) {
        const parsedAgreements = result.data.map(agreement => parseSections(agreement));
        setAgreements(parsedAgreements);
      } else {
        showToast(result.message || "Failed to fetch agreements", "error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showToast("Network error while fetching agreements", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAgreement = (agreement) => {
    const parsed = parseSections(agreement);
    setViewingAgreement(parsed);
    setAcceptTerms(false);
    setShowViewModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingAgreement(null);
    setAcceptTerms(false);
    document.body.style.overflow = 'auto';
  };

  const handleAcceptAgreement = () => {
    if (acceptTerms) {
      showToast("Agreement accepted successfully!");
      closeViewModal();
    } else {
      showToast("Please accept the terms to continue", "error");
    }
  };

  const handleOpenModal = (agreement = null) => {
    if (agreement) {
      const parsedAgreement = parseSections(agreement);
      
      setEditingAgreement(parsedAgreement);
      setFormData({
        id: parsedAgreement.id,
        title: parsedAgreement.title || "",
        type: getTypeValue(parsedAgreement.type) || "business",
        status: parsedAgreement.status || "1",
        sections: parsedAgreement.sections || [],
        hide: parsedAgreement.hide !== undefined ? parsedAgreement.hide : 0
      });
    } else {
      setEditingAgreement(null);
      setFormData({
        id: null,
        title: "",
        type: "business",
        status: "1",
        sections: [],
        hide: 0
      });
    }
    setShowModal(true);
  };

  const handleAddSection = () => {
    setCurrentSection({ title: "", content: "" });
    setEditingSectionIndex(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (index) => {
    setCurrentSection({ ...formData.sections[index] });
    setEditingSectionIndex(index);
    setShowSectionModal(true);
  };

  const handleDeleteSection = (index) => {
    const newSections = [...formData.sections];
    newSections.splice(index, 1);
    setFormData({ ...formData, sections: newSections });
    showToast("Section deleted", "success");
  };

  const handleSaveSection = () => {
    if (!currentSection.title.trim()) {
      showToast("Section title is required", "error");
      return;
    }

    if (editingSectionIndex !== null) {
      const newSections = [...formData.sections];
      newSections[editingSectionIndex] = { ...currentSection };
      setFormData({ ...formData, sections: newSections });
      showToast("Section updated successfully");
    } else {
      setFormData({ 
        ...formData, 
        sections: [...formData.sections, { ...currentSection }] 
      });
      showToast("Section added successfully");
    }
    setShowSectionModal(false);
    setCurrentSection({ title: "", content: "" });
    setEditingSectionIndex(null);
  };

  const toggleSectionExpand = (index, prefix = "modal") => {
    setExpandedSections(prev => ({
      ...prev,
      [`${prefix}_${index}`]: !prev[`${prefix}_${index}`]
    }));
  };

  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const handleSaveAgreement = async () => {
    if (!formData.title.trim()) {
      showToast("Agreement title is required", "error");
      return;
    }

    if (formData.sections.length === 0) {
      showToast("Please add at least one section", "error");
      return;
    }

    setSubmitting(true);
    
    try {
      const submitData = {
        title: formData.title.trim(),
        type: formData.type,
        status: formData.status,
        sections: JSON.stringify(formData.sections),
        hide: formData.hide || 0
      };

      let result;
      if (editingAgreement && formData.id) {
        submitData.id = formData.id;
        result = await postData("mouagreements/update_page", submitData);
        if (result.status) {
          showToast("Agreement updated successfully");
          await fetchAllAgreements();
          setShowModal(false);
          setFormData({ id: null, title: "", type: "business", status: "1", sections: [], hide: 0 });
          setEditingAgreement(null);
        } else {
          showToast(result.message || "Update failed", "error");
        }
      } else {
        result = await postData("mouagreements/add_page", submitData);
        if (result.status) {
          showToast("Agreement added successfully");
          await fetchAllAgreements();
          setShowModal(false);
          setFormData({ id: null, title: "", type: "business", status: "1", sections: [], hide: 0 });
          setEditingAgreement(null);
        } else {
          showToast(result.message || "Add failed", "error");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      showToast(error.message || "Network error while saving", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (agreement) => {
    const newStatus = agreement.status === "1" ? "0" : "1";
    
    setSubmitting(true);
    try {
      const result = await postData("mouagreements/update_status_page", { 
        id: agreement.id, 
        status: newStatus 
      });
      if (result.status) {
        showToast(`Agreement ${newStatus === "1" ? "activated" : "deactivated"} successfully`);
        await fetchAllAgreements();
      } else {
        showToast(result.message || "Status update failed", "error");
      }
    } catch (error) {
      console.error("Status update error:", error);
      showToast(error.message || "Network error while updating status", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgreement = async (id) => {
    if (window.confirm("Are you sure you want to delete this agreement? This action cannot be undone.")) {
      setSubmitting(true);
      try {
        const result = await postData("mouagreements/delete_page", { id });
        if (result.status) {
          showToast("Agreement deleted successfully");
          await fetchAllAgreements();
        } else {
          showToast(result.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("Delete error:", error);
        showToast(error.message || "Network error while deleting", "error");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const filteredAgreements = agreements.filter(agreement => 
    agreement.title?.toLowerCase().includes(search.toLowerCase()) ||
    agreement.type?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const cleanHtmlContent = (content) => {
    if (!content) return "No content";
    return content;
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: t.bg,
      fontFamily: "'Inter', 'DM Sans', sans-serif",
      padding: isMobile ? "16px" : "24px",
    },
    contentWrapper: {
      maxWidth: "1000px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "32px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "16px",
    },
    headerLeft: {
      flex: 1,
    },
    title: {
      fontSize: isMobile ? "24px" : "32px",
      fontWeight: 700,
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: 0,
    },
    subtitle: {
      fontSize: "14px",
      color: t.textMuted,
      marginTop: "4px",
    },
    headerActions: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    searchWrapper: {
      position: "relative",
      width: isMobile ? "100%" : "280px",
    },
    searchInput: {
      width: "100%",
      padding: "10px 12px 10px 36px",
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      color: t.text,
      outline: "none",
      transition: "all 0.2s",
    },
    addButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    // Vertical Card Layout
    cardList: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    agreementCard: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "16px",
      padding: "20px 24px",
      boxShadow: t.shadowSm,
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "16px",
    },
    cardLeft: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
      flex: 1,
      minWidth: "200px",
    },
    cardIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      background: t.accent + "15",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      flexShrink: 0,
    },
    cardContent: {
      flex: 1,
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: t.text,
      margin: 0,
      marginBottom: "4px",
    },
    cardInfo: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    cardMeta: {
      display: "flex",
      gap: "16px",
      fontSize: "12px",
      color: t.textMuted,
      flexWrap: "wrap",
    },
    cardMetaItem: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    cardRight: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    cardActions: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    },
    actionButton: {
      padding: "6px 14px",
      borderRadius: "8px",
      border: "none",
      fontSize: "12px",
      fontWeight: 500,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      transition: "all 0.2s",
    },
    cardMenu: {
      position: "relative",
      cursor: "pointer",
      padding: "4px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    dropdownMenu: {
      position: "absolute",
      top: "100%",
      right: 0,
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      boxShadow: t.shadow,
      minWidth: "160px",
      zIndex: 10,
      padding: "6px 0",
      marginTop: "4px",
    },
    dropdownItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 16px",
      fontSize: "13px",
      color: t.text,
      cursor: "pointer",
      background: "transparent",
      border: "none",
      width: "100%",
      textAlign: "left",
      transition: "background 0.2s",
    },
    metaBadge: {
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 500,
    },
    // Modal Styles
    modalOverlay: {
      position: "fixed",
      inset: 0,
      zIndex: 200,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    },
    modal: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "24px",
      width: isMobile ? "95%" : "900px",
      maxHeight: "85vh",
      overflow: "auto",
    },
    fullPageViewOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      background: t.bg,
      overflowY: "auto",
    },
    fullPageViewContainer: {
      maxWidth: "1000px",
      margin: "0 auto",
      padding: isMobile ? "20px" : "48px",
      minHeight: "100vh",
    },
    fullPageHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "32px",
      paddingBottom: "20px",
      borderBottom: `2px solid ${t.border}`,
      position: "sticky",
      top: 0,
      background: t.bg,
      zIndex: 10,
    },
    fullPageTitle: {
      fontSize: isMobile ? "20px" : "24px",
      fontWeight: 700,
      color: t.text,
    },
    closeButton: {
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      cursor: "pointer",
      padding: "10px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: t.textMuted,
    },
    previewHeader: {
      textAlign: "center",
      marginBottom: "40px",
      paddingBottom: "24px",
      borderBottom: `2px solid ${t.border}`,
    },
    previewTitle: {
      fontSize: isMobile ? "28px" : "36px",
      fontWeight: 700,
      color: t.text,
      marginBottom: "12px",
    },
    previewSubtitle: {
      fontSize: "14px",
      color: t.textMuted,
    },
    previewSection: {
      marginBottom: "32px",
      paddingBottom: "24px",
      borderBottom: `1px solid ${t.border}`,
    },
    previewSectionTitle: {
      fontSize: "20px",
      fontWeight: 600,
      color: t.accent,
      marginBottom: "16px",
      paddingLeft: "12px",
      borderLeft: `4px solid ${t.accent}`,
    },
    previewContent: {
      fontSize: "15px",
      color: t.textSub,
      lineHeight: "1.8",
      padding: "0 12px",
    },
    acceptCheckbox: {
      marginTop: "40px",
      padding: "20px 24px",
      background: t.surfaceAlt,
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      border: `1px solid ${t.border}`,
    },
    fullPageFooter: {
      display: "flex",
      gap: "16px",
      justifyContent: "flex-end",
      marginTop: "40px",
      paddingTop: "24px",
      borderTop: `1px solid ${t.border}`,
    },
    modalHeader: {
      padding: "20px 24px",
      borderBottom: `1px solid ${t.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "sticky",
      top: 0,
      background: t.surface,
      zIndex: 10,
    },
    modalTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: t.text,
    },
    modalBody: {
      padding: "24px",
    },
    modalFooter: {
      padding: "16px 24px",
      borderTop: `1px solid ${t.border}`,
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      position: "sticky",
      bottom: 0,
      background: t.surface,
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: t.textMuted,
      marginBottom: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      fontFamily: "inherit",
    },
    select: {
      width: "100%",
      padding: "12px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      cursor: "pointer",
    },
    textarea: {
      width: "100%",
      padding: "12px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      fontFamily: "inherit",
      resize: "vertical",
      minHeight: "180px",
    },
    cancelBtn: {
      padding: "10px 24px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      color: t.text,
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 500,
    },
    saveBtn: {
      padding: "10px 24px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "13px",
    },
    loadingText: {
      textAlign: "center",
      padding: "60px",
      color: t.textMuted,
      fontSize: "14px",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px",
      color: t.textMuted,
      fontSize: "14px",
    },
    sectionCard: {
      background: t.surfaceAlt,
      borderRadius: "12px",
      marginBottom: "12px",
      border: `1px solid ${t.border}`,
      overflow: "hidden",
    },
    sectionHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      cursor: "pointer",
      background: t.surface,
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: t.text,
    },
    sectionContent: {
      padding: "16px",
      fontSize: "13px",
      color: t.textSub,
      lineHeight: "1.6",
      borderTop: `1px solid ${t.border}`,
    },
    sectionActions: {
      display: "flex",
      gap: "8px",
    },
    sectionButton: {
      padding: "4px 8px",
      borderRadius: "6px",
      border: "none",
      fontSize: "11px",
      cursor: "pointer",
    },
    addSectionBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 16px",
      background: "transparent",
      border: `2px dashed ${t.border}`,
      borderRadius: "12px",
      color: t.accent,
      fontSize: "13px",
      fontWeight: 500,
      cursor: "pointer",
      width: "100%",
      justifyContent: "center",
      marginTop: "16px",
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>📄 MOU Agreements</h1>
            <p style={styles.subtitle}>Manage memorandum of understanding for partners</p>
          </div>
          <div style={styles.headerActions}>
            <div style={styles.searchWrapper}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }}><SearchIcon /></span>
              <input 
                style={styles.searchInput} 
                placeholder="Search agreements..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
            <button style={styles.addButton} onClick={() => handleOpenModal()}>
              <PlusIcon /> Add Agreement
            </button>
          </div>
        </div>

        {/* Vertical Card List */}
        {loading ? (
          <div style={styles.loadingText}>Loading agreements...</div>
        ) : filteredAgreements.length === 0 ? (
          <div style={styles.emptyState}>No agreements found</div>
        ) : (
          <div style={styles.cardList}>
            {filteredAgreements.map((agreement) => {
              const typeInfo = getTypeColor(agreement.type);
              return (
                <div key={agreement.id} style={styles.agreementCard}>
                  {/* Left Section */}
                  <div style={styles.cardLeft}>
                    <div style={styles.cardIcon}>
                      📄
                    </div>
                    <div style={styles.cardContent}>
                      <h3 style={styles.cardTitle}>{agreement.title}</h3>
                      <div style={styles.cardInfo}>
                        <StatusBadge status={agreement.status} isDark={isDark} />
                        <span style={{
                          ...styles.metaBadge,
                          background: typeInfo.bg,
                          color: typeInfo.color
                        }}>
                          {getTypeLabel(agreement.type)}
                        </span>
                        <span style={styles.cardMetaItem}>
                          📑 {agreement.sections?.length || 0} sections
                        </span>
                        <span style={styles.cardMetaItem}>
                          📅 {formatDate(agreement.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Section */}
                  <div style={styles.cardRight}>
                    <div style={styles.cardActions}>
                      <button 
                        style={{...styles.actionButton, background: t.accent + "20", color: t.accent}}
                        onClick={() => handleViewAgreement(agreement)}
                      >
                        <EyeIcon size={14} /> View
                      </button>
                      <button 
                        style={{...styles.actionButton, background: t.info + "20", color: t.info}}
                        onClick={() => handleOpenModal(agreement)}
                      >
                        <EditIcon size={14} /> Edit
                      </button>
                      <button 
                        style={{
                          ...styles.actionButton, 
                          background: agreement.status === "1" ? t.warning + "20" : t.success + "20",
                          color: agreement.status === "1" ? t.warning : t.success
                        }}
                        onClick={() => handleToggleStatus(agreement)}
                      >
                        {agreement.status === "1" ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                    <div style={styles.cardMenu} onClick={() => toggleDropdown(agreement.id)}>
                      <MenuIcon />
                      {dropdownOpen === agreement.id && (
                        <div style={styles.dropdownMenu}>
                          <button style={styles.dropdownItem} onClick={() => handleViewAgreement(agreement)}>
                            <EyeIcon /> View
                          </button>
                          <button style={styles.dropdownItem} onClick={() => handleOpenModal(agreement)}>
                            <EditIcon /> Edit
                          </button>
                          <button style={styles.dropdownItem} onClick={() => handleToggleStatus(agreement)}>
                            {agreement.status === "1" ? "Deactivate" : "Activate"}
                          </button>
                          <button style={{...styles.dropdownItem, color: t.danger}} onClick={() => handleDeleteAgreement(agreement.id)}>
                            <DeleteIcon /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full Page View Agreement Modal */}
      {showViewModal && viewingAgreement && (
        <div style={styles.fullPageViewOverlay}>
          <div style={styles.fullPageViewContainer}>
            <div style={styles.fullPageHeader}>
              <div style={styles.fullPageTitle}>Preview Agreement</div>
              <button onClick={closeViewModal} style={styles.closeButton}>
                <CloseIcon />
              </button>
            </div>

            <div style={styles.previewHeader}>
              <div style={styles.previewTitle}>{viewingAgreement.title}</div>
              <div style={styles.previewSubtitle}>
                Zaptodoor {getTypeLabel(viewingAgreement.type)} Agreement
              </div>
            </div>

            {viewingAgreement.sections?.map((section, idx) => (
              <div key={idx} style={styles.previewSection}>
                <div style={styles.previewSectionTitle}>{section.title}</div>
                <div 
                  style={styles.previewContent}
                  dangerouslySetInnerHTML={{ 
                    __html: cleanHtmlContent(section.content)
                  }}
                />
              </div>
            ))}

            <div style={styles.acceptCheckbox}>
              <input 
                type="checkbox" 
                id="acceptTerms" 
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <label htmlFor="acceptTerms" style={{ fontSize: "14px", color: t.text, cursor: "pointer" }}>
                I have read and agree to the terms of this MOU
              </label>
            </div>

            <div style={styles.fullPageFooter}>
              <button style={styles.cancelBtn} onClick={closeViewModal}>Close</button>
              <button 
                style={styles.saveBtn} 
                onClick={handleAcceptAgreement}
              >
                Accept Agreement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Agreement Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>{editingAgreement ? "Edit MOU Agreement" : "Create MOU Agreement"}</div>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}>
                <CloseIcon />
              </button>
            </div>
            <div style={styles.modalBody}>
              <h3 style={{ fontSize: "14px", marginBottom: "16px", color: t.text }}>Agreement Details</h3>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Agreement Title *</label>
                <input 
                  style={styles.input} 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Memorandum of Understanding"
                />
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Type</label>
                  <select 
                    style={styles.select} 
                    value={formData.type} 
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="business">Business / Restaurant</option>
                    <option value="delivery">Delivery Partner</option>
                    <option value="vendor">Vendor</option>
                    <option value="Rider / Driver">Rider / Driver</option>
                    <option value="User / Customer">User / Customer</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.select} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: "24px" }}>
                <h3 style={{ fontSize: "14px", marginBottom: "16px", color: t.text }}>
                  Agreement Sections ({formData.sections.length} section{formData.sections.length !== 1 ? "s" : ""})
                </h3>
                
                {formData.sections.map((section, idx) => (
                  <div key={idx} style={styles.sectionCard}>
                    <div 
                      style={styles.sectionHeader}
                      onClick={() => toggleSectionExpand(idx, "modal")}
                    >
                      <div>
                        <span style={styles.sectionTitle}>{section.title || "(untitled section)"}</span>
                      </div>
                      <div style={styles.sectionActions}>
                        <button 
                          style={{ ...styles.sectionButton, background: t.info, color: "#fff" }}
                          onClick={(e) => { e.stopPropagation(); handleEditSection(idx); }}
                        >
                          Edit
                        </button>
                        <button 
                          style={{ ...styles.sectionButton, background: t.danger, color: "#fff" }}
                          onClick={(e) => { e.stopPropagation(); handleDeleteSection(idx); }}
                        >
                          Delete
                        </button>
                        {expandedSections[`modal_${idx}`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      </div>
                    </div>
                    {expandedSections[`modal_${idx}`] && (
                      <div style={styles.sectionContent}>
                        <div dangerouslySetInnerHTML={{ __html: section.content || "No content" }} />
                      </div>
                    )}
                  </div>
                ))}

                <button style={styles.addSectionBtn} onClick={handleAddSection}>
                  <PlusIcon /> Add Section
                </button>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSaveAgreement} disabled={submitting}>
                {submitting ? "Saving..." : (editingAgreement ? "Update Agreement" : "Create Agreement")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Edit Modal */}
      {showSectionModal && (
        <div style={styles.modalOverlay} onClick={() => setShowSectionModal(false)}>
          <div style={{ ...styles.modal, width: isMobile ? "95%" : "700px" }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>{editingSectionIndex !== null ? "Edit Section" : "Add New Section"}</div>
              <button onClick={() => setShowSectionModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}>
                <CloseIcon />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Section Title *</label>
                <input 
                  style={styles.input} 
                  value={currentSection.title} 
                  onChange={(e) => setCurrentSection({ ...currentSection, title: e.target.value })}
                  placeholder="e.g. 1. Partnership Terms"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Content</label>
                <textarea
                  style={styles.textarea}
                  value={currentSection.content}
                  onChange={(e) => setCurrentSection({ ...currentSection, content: e.target.value })}
                  placeholder="Write section content here..."
                  rows={8}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowSectionModal(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSaveSection}>Save Section</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
    </div>
  );
};

export default MOUAgreements;